/**
 * Parlay Builder Store (Zustand)
 *
 * Manages global state for building custom parlays with real-time validation and scoring.
 * Migrated from ParlayBuilderContext for better performance (selective subscriptions)
 * and simpler component access (no provider requirement).
 *
 * Max leg limits are per-sportsbook (e.g. FanDuel=25, DraftKings=12) via getMaxLegsForSportsbook().
 */

import { SPORTSBOOKS } from '@config/constants';
import type { NewParlay, NewParlayLeg } from '@hooks/useTrackedParlays';
import type { AnalysisConfig } from '@parlay-picker/shared/analysisConfig';
import type { AddAllLegsResult, AddLegResult, BuilderLeg } from '@parlay-picker/shared/builderLeg';
import {
  type BlockCheck,
  type CorrelationWarning,
  detectCorrelationWarnings,
  filterBlockedLegs,
  isLegBlocked,
} from '@parlay-picker/shared/correlationUtils';
import { deriveParlayLeague } from '@parlay-picker/shared/league';
import type { ActionableSuggestion, ParlayAnalysis } from '@parlay-picker/shared/parlayAnalysis';
import {
  applyAnalyzerSuggestion,
  buildPropString,
  calculateCombinedOdds,
  generateLegId,
  isDuplicateLeg,
} from '@parlay-picker/shared/parlayBuilderCore';
import type { QuotaCoinData } from '@parlay-picker/shared/quotaCoin';
import { getMaxLegsForSportsbook } from '@parlay-picker/shared/sportsbook';
import { getUserFriendlyError } from '@utils/api';
import { create } from 'zustand';
import { API_ENDPOINTS, apiUrl } from '../config/api';

// =============================================================================
// Types — BuilderLeg, AddLegResult, AddAllLegsResult from @parlay-picker/shared/builderLeg
// =============================================================================

// Type for authFetch function (passed from useAuth)
type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

// =============================================================================
// Constants
// =============================================================================

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Convert BuilderLegs to correlation format for validation
 */
function toCorrelationLeg(leg: Partial<BuilderLeg>) {
  return {
    playerName: leg.player,
    team: leg.team,
    propType: leg.statType,
    overUnder: leg.overUnder,
    line: leg.line,
    gameId: leg.gameId,
    sportsbook: leg.sportsbook,
  };
}

// =============================================================================
// Store Interface
// =============================================================================

interface ParlayBuilderState {
  // State
  legs: BuilderLeg[];
  isExpanded: boolean;
  analysisConfig: AnalysisConfig | null;

  // AI Analysis state
  analysis: ParlayAnalysis | null;
  /** The factor toggles that shaped `analysis`; a leg it swaps in or re-lines records them. */
  analysisFactorsUsed: AnalysisConfig | null;
  isAnalyzing: boolean;
  analysisError: string | null;

  // Derived values (computed on access)
  getSportsbook: () => string | null;
  getMaxLegs: () => number;
  getTotalOdds: () => string | null;
  getLegCount: () => number;
  getWarnings: () => CorrelationWarning[];
  getStartedLegs: () => BuilderLeg[]; // Legs with games that have already started

  // Actions
  addLeg: (leg: BuilderLeg) => AddLegResult;
  addAllLegs: (legs: BuilderLeg[]) => AddAllLegsResult;
  removeLeg: (legId: string) => void;
  clearAll: () => void;
  toggleExpanded: () => void;
  setExpanded: (expanded: boolean) => void;

  // Validation
  isBlocked: (leg: Partial<BuilderLeg>) => BlockCheck;

  // Checks
  hasLeg: (legId: string) => boolean;
  isLegInBuilder: (
    player: string | null | undefined,
    statType: string,
    line: number | null,
  ) => boolean;

  // Track action
  buildTrackPayload: () => NewParlay | null; // null when no leg names a book

  // Analysis config
  setAnalysisConfig: (config: AnalysisConfig | null) => void;

  // AI Analysis (authFetch passed as parameter since we can't use hooks in stores)
  analyzeParlay: (
    authFetch: AuthFetch,
    onQuotaUsed?: (data: QuotaCoinData) => void,
  ) => Promise<void>;
  clearAnalysis: () => void;
  applySuggestion: (suggestion: ActionableSuggestion) => void;
}

// =============================================================================
// Store Implementation
// =============================================================================

export const useParlayBuilderStore = create<ParlayBuilderState>((set, get) => ({
  // Initial state
  legs: [],
  isExpanded: false,
  analysisConfig: null,
  analysis: null,
  analysisFactorsUsed: null,
  isAnalyzing: false,
  analysisError: null,

  // Derived values
  getSportsbook: () => {
    const { legs } = get();
    return legs.length > 0 ? legs[0].sportsbook : null;
  },

  getMaxLegs: () => {
    const sportsbook = get().legs[0]?.sportsbook ?? null;
    return getMaxLegsForSportsbook(sportsbook);
  },

  getTotalOdds: () => calculateCombinedOdds(get().legs),

  getLegCount: () => get().legs.length,

  getWarnings: () => {
    const { legs } = get();
    if (legs.length < 2) return [];
    const correlationLegs = legs.map(toCorrelationLeg);
    return detectCorrelationWarnings(correlationLegs);
  },

  getStartedLegs: () => {
    const { legs } = get();
    const now = new Date();
    return legs.filter((leg) => {
      if (!leg.gameStartsAt) return false;
      return new Date(leg.gameStartsAt) <= now;
    });
  },

  // Check if a leg would be blocked
  isBlocked: (leg: Partial<BuilderLeg>): BlockCheck => {
    const { legs } = get();
    const sportsbook = legs.length > 0 ? legs[0].sportsbook : null;
    const correlationLeg = toCorrelationLeg(leg);
    const existingLegs = legs.map(toCorrelationLeg);
    return isLegBlocked(correlationLeg, existingLegs, sportsbook);
  },

  // Add a single leg
  addLeg: (leg: BuilderLeg): AddLegResult => {
    const { legs, isBlocked, analysisConfig } = get();

    // Check max leg limit (per-sportsbook)
    const currentSportsbook = legs[0]?.sportsbook ?? leg.sportsbook ?? null;
    const maxLegs = getMaxLegsForSportsbook(currentSportsbook);
    if (legs.length >= maxLegs) {
      return {
        success: false,
        error: `Maximum ${maxLegs} legs allowed per parlay on ${SPORTSBOOKS[currentSportsbook ?? '']?.name ?? 'this sportsbook'}`,
        blocked: true,
      };
    }

    // Check if blocked
    const blockCheck = isBlocked(leg);
    if (!blockCheck.canAdd) {
      return {
        success: false,
        error: blockCheck.blockReason,
        blocked: true,
      };
    }

    // Generate ID if not provided, snapshot current analysisConfig onto the leg
    const legWithId: BuilderLeg = {
      ...leg,
      id: leg.id || generateLegId(),
      analysisConfig: leg.analysisConfig ?? analysisConfig,
    };

    set((state) => ({
      legs: [...state.legs, legWithId],
      // Auto-expand when first leg is added
      isExpanded: state.legs.length === 0 ? true : state.isExpanded,
      // Clear analysis since parlay changed
      analysis: null,
      analysisError: null,
    }));

    return { success: true };
  },

  // Add multiple legs at once (filters out blocked ones)
  addAllLegs: (newLegs: BuilderLeg[]): AddAllLegsResult => {
    const { legs, analysisConfig } = get();
    const sportsbook = legs.length > 0 ? legs[0].sportsbook : null;

    // Check how many slots are available (per-sportsbook limit)
    const effectiveSportsbook = sportsbook ?? newLegs[0]?.sportsbook ?? null;
    const maxLegs = getMaxLegsForSportsbook(effectiveSportsbook);
    const availableSlots = maxLegs - legs.length;
    if (availableSlots <= 0) {
      const bookName = SPORTSBOOKS[effectiveSportsbook ?? '']?.name ?? 'this sportsbook';
      return {
        addedCount: 0,
        blockedCount: newLegs.length,
        blockedReasons: [`Maximum ${maxLegs} legs allowed per parlay on ${bookName}`],
      };
    }

    // Convert to correlation format
    const newCorrelationLegs = newLegs.map((leg) => ({
      ...leg,
      playerName: leg.player,
      propType: leg.statType,
    }));

    const existingCorrelationLegs = legs.map(toCorrelationLeg);
    const result = filterBlockedLegs(newCorrelationLegs, existingCorrelationLegs, sportsbook);

    // Limit to available slots
    const legsToAdd = result.added.slice(0, availableSlots);
    const blockedByLimit = result.added.length - legsToAdd.length;

    if (legsToAdd.length > 0) {
      // Add IDs to legs that don't have them, snapshot current analysisConfig
      const legsWithIds = legsToAdd.map((leg) => ({
        ...leg,
        id: (leg as BuilderLeg).id || generateLegId(),
        analysisConfig: (leg as BuilderLeg).analysisConfig ?? analysisConfig,
      })) as BuilderLeg[];

      set((state) => ({
        legs: [...state.legs, ...legsWithIds],
        // Auto-expand when legs are added
        isExpanded: state.legs.length === 0 ? true : state.isExpanded,
        // Clear analysis since parlay changed
        analysis: null,
        analysisError: null,
      }));
    }

    return {
      addedCount: legsToAdd.length,
      blockedCount: result.blocked.length + blockedByLimit,
      blockedReasons: [
        ...result.blocked.map((b) => b.reason),
        ...(blockedByLimit > 0 ? [`${blockedByLimit} leg(s) exceeded max limit`] : []),
      ],
    };
  },

  // Remove a leg by ID
  removeLeg: (legId: string) => {
    set((state) => ({
      legs: state.legs.filter((l) => l.id !== legId),
      // Clear analysis since parlay changed
      analysis: null,
      analysisError: null,
    }));
  },

  // Clear all legs
  clearAll: () => {
    set({
      legs: [],
      isExpanded: false,
      analysis: null,
      analysisError: null,
    });
  },

  // Toggle expanded state
  toggleExpanded: () => {
    set((state) => ({ isExpanded: !state.isExpanded }));
  },

  // Set expanded state
  setExpanded: (expanded: boolean) => {
    set({ isExpanded: expanded });
  },

  // Check if a leg is in the builder by ID
  hasLeg: (legId: string): boolean => {
    return get().legs.some((l) => l.id === legId);
  },

  // Check if a leg with matching criteria is in the builder
  isLegInBuilder: (
    player: string | null | undefined,
    statType: string,
    line: number | null,
  ): boolean => {
    return isDuplicateLeg(get().legs, player, statType, line);
  },

  // Build payload for tracking
  buildTrackPayload: (): NewParlay | null => {
    const { legs, analysis, analysisConfig, getTotalOdds, getSportsbook } = get();
    const sportsbook = getSportsbook();
    if (!sportsbook) return null;

    // Build origins array from unique leg sources
    const sources = new Set(legs.map((l) => l.source));
    const origins: Array<'ai' | 'manual'> = Array.from(sources) as Array<'ai' | 'manual'>;

    // Build legs for tracking
    const trackLegs: NewParlayLeg[] = legs.map((leg) => ({
      player: leg.player ?? leg.team, // Use team name for team bets
      playerId: leg.playerId,
      team: leg.team,
      opponentTeam: leg.opponentTeam,
      prop: buildPropString(leg),
      statType: leg.statType,
      line: leg.line,
      overUnder: leg.overUnder,
      odds: leg.odds,
      gameId: leg.gameId,
      gameDate: leg.gameDate ?? undefined,
      gameStartsAt: leg.gameStartsAt ?? undefined,
      marketKey: leg.marketKey ?? undefined, // Critical for settlement (standard vs alternate)
      isB2b: leg.isB2b,
      blowoutRisk: leg.blowoutRisk,
      isHomeGame: leg.isHomeGame ?? null,
      // Per-leg config snapshot (captured when leg was added to builder)
      analysisConfig: leg.analysisConfig ?? null,
      leagueKey: leg.leagueKey,
      provenance: leg.provenance,
      predictionId: leg.predictionId,
      factorsUsed: leg.factorsUsed,
    }));

    // Name based on leg count - single leg = straight bet
    const name = legs.length === 1 ? 'Straight Bet' : `Custom ${legs.length}-Leg Parlay`;

    const totalOdds = getTotalOdds();

    return {
      name,
      totalOdds: totalOdds ?? undefined,
      sportsbook,
      origins, // Array of sources: ['ai'], ['manual'], or ['ai', 'manual']
      isAIImproved: sources.has('ai') && sources.has('manual'), // Mixed sources
      legs: trackLegs,
      // Include AI analysis verdict if available (for tracking accuracy)
      aiAnalysisVerdict: analysis?.verdict,
      // Include analysis config for factor flags + enrichment gating
      analysisConfig: analysisConfig ?? undefined,
      // Derive league from per-leg leagueKeys (supports cross-league parlays)
      league: deriveParlayLeague(legs.map((l) => l.leagueKey)),
    };
  },

  // Set analysis config
  setAnalysisConfig: (config: AnalysisConfig | null) => {
    set({ analysisConfig: config });
  },

  // Clear analysis manually
  clearAnalysis: () => {
    set({ analysis: null, analysisError: null });
  },

  // Analyze the current parlay using AI
  // authFetch is passed as parameter since we can't use React hooks in Zustand
  analyzeParlay: async (
    authFetch: AuthFetch,
    onQuotaUsed?: (data: QuotaCoinData) => void,
  ): Promise<void> => {
    const { legs, analysisConfig, getSportsbook } = get();
    const sportsbook = getSportsbook();

    if (legs.length === 0 || !sportsbook) {
      set({ analysisError: 'Add legs to your parlay before analyzing' });
      return;
    }

    set({ isAnalyzing: true, analysisError: null });

    try {
      // Convert BuilderLeg to AnalysisLeg format for the API
      const analysisLegs = legs.map((leg) => ({
        id: leg.id, // Pass leg ID for suggestion targeting
        player: leg.player,
        playerId: leg.playerId,
        team: leg.team,
        opponentTeam: leg.opponentTeam,
        statType: leg.statType,
        line: leg.line,
        overUnder: leg.overUnder,
        odds: leg.odds,
        gameId: leg.gameId,
        gameDate: leg.gameDate,
        sportsbook: leg.sportsbook,
        marketKey: leg.marketKey,
        leagueKey: leg.leagueKey,
        aiAnalysis: leg.aiAnalysis,
        aiConfidence: leg.aiConfidence,
        aiEdge: leg.aiEdge,
        // Edge/trend data for enriched analysis
        primaryEdge: leg.primaryEdge,
        trend: leg.trend,
        average: leg.average,
        riskFactors: leg.riskFactors,
        // Situational context
        isB2b: leg.isB2b,
        blowoutRisk: leg.blowoutRisk,
        isHomeGame: leg.isHomeGame ?? null,
        matchupNote: leg.matchupNote,
        recentForm: leg.recentForm,
        injuryContext: leg.injuryContext,
      }));

      const response = await authFetch(apiUrl(API_ENDPOINTS.PARLAYS_ANALYZE), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          legs: analysisLegs,
          sportsbook,
          analysisConfig, // Gate enrichment by user's config toggles
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Analysis failed: ${response.status}`);
      }

      const data = await response.json();
      if (data.analysis) {
        set({
          analysis: data.analysis,
          analysisFactorsUsed: data.factorsUsed ?? null,
          isAnalyzing: false,
        });
        if (data.quota && onQuotaUsed) {
          onQuotaUsed(data.quota);
        }
      } else {
        throw new Error('No analysis returned');
      }
    } catch (error) {
      const message = getUserFriendlyError(error as Error);
      set({ analysisError: message, isAnalyzing: false });
    }
  },

  // Apply a suggestion from the AI analysis
  applySuggestion: (suggestion: ActionableSuggestion): void => {
    const { legs, analysisConfig, analysisFactorsUsed } = get();
    const newLegs = applyAnalyzerSuggestion(legs, suggestion, {
      analysisConfig,
      factorsUsed: analysisFactorsUsed,
    });

    // Update legs and clear analysis since parlay changed
    set({
      legs: newLegs,
      analysis: null,
      analysisError: null,
    });
  },
}));

// =============================================================================
// Selector Hooks (for convenience and optimized re-renders)
// =============================================================================

export const useBuilderLegs = (): BuilderLeg[] => useParlayBuilderStore((s) => s.legs);

export const useBuilderIsExpanded = (): boolean => useParlayBuilderStore((s) => s.isExpanded);

export const useBuilderAnalysis = (): ParlayAnalysis | null =>
  useParlayBuilderStore((s) => s.analysis);

export const useBuilderIsAnalyzing = (): boolean => useParlayBuilderStore((s) => s.isAnalyzing);

export const useBuilderAnalysisError = (): string | null =>
  useParlayBuilderStore((s) => s.analysisError);
